<template>
  <div ref="containerRef" :style="styles.container" @pointerdown.passive="jump">
    <div ref="worldRef" :style="worldStyle">
      <!-- Céu decorativo -->
      <div :style="skyStyle">
        <div :style="cloud(24, 24, 96, 96, 0.7)"></div>
        <div :style="cloud(120, 40, 112, 72, 0.6)"></div>
        <div :style="cloud(worldW - 200, 28, 120, 80, 0.6)"></div>
      </div>

      <!-- Chão -->
      <div :style="styles.ground">
        <div :style="styles.groundPattern"></div>
      </div>

      <!-- Cavalo (virado para a direita) -->
      <div :style="horseStyle" aria-label="Cavalo">
        <span :style="{ display: 'inline-block', transform: 'translateY(-4px) scaleX(-1)', fontSize: fontScale + 'px' }">🐴</span>
      </div>

      <!-- Obstáculos: barras ou água -->
      <div
        v-for="(o, i) in obstacles"
        :key="i"
        :style="o.type === 'water' ? waterStyle(o) : barStyle(o)"
      >
        <div v-if="o.type !== 'water'" :style="obstacleTopStyle" />
      </div>

      <!-- HUD -->
      <div :style="styles.hud">
        <div :style="styles.hudChip">Pontos: <b>{{ score }}</b></div>
        <div :style="styles.hudChip">Recorde: <b>{{ best }}</b></div>
      </div>

      <!-- Tela inicial -->
      <div v-if="!running && !gameOver" :style="styles.startOverlay">
        <h1 :style="{ fontSize: '22px', margin: 0 }">Cavalo Saltador 🐴</h1>
        <p :style="{ margin: 0, fontSize: '16px' }">
          Toque/click ou pressione
          <kbd style="padding:2px 6px;background:rgba(255,255,255,.7);border-radius:6px;border:1px solid #e5e7eb;color:#000;">
            Espaço
          </kbd>para pular os obstáculos. Evite bater!
        </p>
        <button :style="styles.startBtn" @click="resetGame" @pointerdown.prevent.stop="resetGame">Começar</button>
      </div>

      <!-- Game Over -->
      <div v-if="gameOver" :style="styles.over">
        <div :style="{ fontSize: Math.max(20, Math.round(worldH * 0.12)) + 'px', fontWeight: 800 }">Game Over</div>
        <div :style="{ fontSize: Math.max(12, Math.round(worldH * 0.06)) + 'px' }">Pontos: {{ score }} · Recorde: {{ best }}</div>
        <div style="display:flex;gap:8px;margin-top:6px">
          <button :style="styles.overBtn" @click="resetGame" @pointerdown.prevent.stop="resetGame">Jogar de novo</button>
        </div>
        <div style="margin-top:6px;font-size:12px;opacity:.85">Dica: toque em qualquer lugar para pular</div>
      </div>

      <!-- (Opcional) Botão de pulo no mobile -->
      <button v-if="running && !gameOver" :style="styles.jumpBtn" @pointerdown.prevent.stop="jump" aria-label="Pular">⤴︎</button>
     
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, reactive, ref, watch, computed } from 'vue'

/* ========= Estado básico ========= */
const containerRef = ref(null)
const worldRef = ref(null)
const worldW = ref(720)
const worldH = ref(300)

const dims = reactive({ GROUND_HEIGHT: 40, HORSE_W: 44, HORSE_H: 40, HORSE_X: 80 })
const running = ref(false)
const gameOver = ref(false)
const score = ref(0)
const best = ref(Number(localStorage.getItem('cavalo_best') || 0))
const obstacles = ref([])

const horse = reactive({ x: 80, y: 0, w: 44, h: 40, vy: 0 })
let speed = 5, frame = 0, rafId = 0

/* ========= Helpers ========= */
const cloud = (left, top, w, h, op) => ({
  position: 'absolute',
  left: left + 'px',
  top: top + 'px',
  width: w + 'px',
  height: h + 'px',
  borderRadius: '999px',
  background: `rgba(255,255,255,${op})`,
  filter: 'blur(1px)'
})
function vibrate(ms = 12) {
  try { if (typeof navigator !== 'undefined' && 'vibrate' in navigator) navigator.vibrate(ms) } catch (e) { /* noop */ }
}
function rand(min, max){ return Math.floor(Math.random() * (max - min + 1)) + min }
// Gap entre obstáculos do mesmo combo — mais largo e cresce com a velocidade
function clusterGapPx(){
  const base = horse.w * 2 + 48
  const speedAdj = Math.round(speed * 8)
  return base + speedAdj + rand(10, 26)
}
const allowTriple = computed(() => worldW.value >= 420)

/* ========= ResizeObserver (altura fixa 320) ========= */
let ro
onMounted(() => {
  let roRaf = 0
  ro = new ResizeObserver((entries) => {
    const el = entries[0].contentRect
    cancelAnimationFrame(roRaf)
    roRaf = requestAnimationFrame(() => {
      const W = Math.max(280, Math.min(960, el.width))
      const H = 320
      if (W !== worldW.value || H !== worldH.value) { worldW.value = W; worldH.value = H }
    })
  })
  if (containerRef.value) ro.observe(containerRef.value)

  document.addEventListener('keydown', onKey, { passive: false })
  document.addEventListener('visibilitychange', onVis)
})

onUnmounted(() => {
  if (ro) ro.disconnect()
  cancelAnimationFrame(rafId)
  document.removeEventListener('keydown', onKey)
  document.removeEventListener('visibilitychange', onVis)
})

/* ========= Reagir a mudanças de tamanho ========= */
watch([worldW, worldH], () => {
  dims.GROUND_HEIGHT = 40
  dims.HORSE_H = 40
  dims.HORSE_W = 44
  dims.HORSE_X = Math.round(worldW.value * 0.12)

  horse.w = dims.HORSE_W
  horse.h = dims.HORSE_H
  horse.x = dims.HORSE_X
  horse.y = worldH.value - dims.GROUND_HEIGHT - dims.HORSE_H

  speed = baseSpeed()
})

/* ========= Game loop ========= */
function loop() {
  frame++
  if (frame % 240 === 0) speed = Math.min(speed + 0.25, 14)

  // física
  horse.vy += 0.6
  horse.y  += horse.vy
  const groundY = worldH.value - dims.GROUND_HEIGHT - horse.h
  if (horse.y > groundY) { horse.y = groundY; horse.vy = 0 }

// gerar obstáculos com água controlada (largura limitada + gaps seguros)
if (frame % 120 === 0) {
  // probabilidade de água diminui com velocidade (mais rápido => menos água)
  const pWater = speed < 7 ? 0.22 : (speed < 9 ? 0.18 : 0.12);

  const r = Math.random();
  let clusterCount = allowTriple.value ? (r < 0.10 ? 3 : r < 0.35 ? 2 : 1)
                                       : (r < 0.30 ? 2 : 1);

  // Se tiver água no combo: máx 2 itens e nunca começa com água (mais jogável)
  const hasWater = Math.random() < pWater;
  if (hasWater) clusterCount = Math.min(clusterCount, 2);

  // Se houver água e clusterCount==2, ela vem em 2º (barra -> água)
  const waterIndex = (hasWater && clusterCount === 2) ? 1 : -1;

  let x = worldW.value + 24;

  for (let i = 0; i < clusterCount; i++) {
    const makeWater = (i === waterIndex) || (hasWater && clusterCount === 1 && i === 0 && false); 
    // ^ o `false` acima garante que single com água não seja obrigatório aqui (mantém só barra na maioria)

    if (makeWater) {
      // LAGUINHO: largura limitada
      const width  = waterWidthPx();  // e.g., 80–150 px (cap por tela)
      const height = 12;

      obstacles.value.push({
        x,
        y: worldH.value - dims.GROUND_HEIGHT - height,
        w: width,
        h: height,
        passed: false,
        type: 'water',
      });

      // Depois da água: respiro + pouso
      x += width + afterWaterGapPx() + landingGapPx();

    } else {
      // BARRA
      const height = 30 + Math.floor(Math.random() * 45);  // 30–75 px
      const width  = 16 + Math.floor(Math.random() * 24);  // 16–40 px

      obstacles.value.push({
        x,
        y: worldH.value - dims.GROUND_HEIGHT - height,
        w: width,
        h: height,
        passed: false,
        type: 'bar',
      });

      // Gap entre barras do mesmo combo
      if (i < clusterCount - 1) {
        x += width + clusterGapPx() + landingGapPx();
      }
    }
  }
}

  // mover e pontuar
  for (const o of obstacles.value) {
    o.x -= speed
    if (!o.passed && o.x + o.w < horse.x) { o.passed = true; score.value++ }
  }
  obstacles.value = obstacles.value.filter(o => o.x + o.w > -10)

  // colisão: hitbox reduzida + regra especial de água
  const padX = horse.w * 0.15
  const padY = horse.h * 0.25
  const hx = horse.x + padX,  hy = horse.y + padY
  const hw = horse.w - padX * 2,  hh = horse.h - padY * 2

  for (const o of obstacles.value) {
    if (o.type === 'water') {
      const overlapX = hx < o.x + o.w && hx + hw > o.x
      const waterTop = o.y
      const cleared = (hy + hh) < (waterTop - 2) // passou por cima da lâmina
      if (overlapX && !cleared) { gameOverNow(); return }
    } else {
      const topPad = 4
      const ox = o.x, oy = o.y + topPad, ow = o.w, oh = o.h - topPad
      const collide = hx < ox + ow && hx + hw > ox && hy < oy + oh && hy + hh > oy
      if (collide) { gameOverNow(); return }
    }
  }

  rafId = requestAnimationFrame(loop)
}

function gameOverNow(){
  running.value = false
  gameOver.value = true
  const newBest = Math.max(best.value, score.value)
  best.value = newBest
  localStorage.setItem('cavalo_best', String(newBest))
  cancelAnimationFrame(rafId)
  vibrate(30)
}

function baseSpeed(){
  return Math.max(3.2, Math.min(12, 5 * (worldW.value / 900)));
}


/* ========= Controles ========= */
function resetGame() {
  horse.x = dims.HORSE_X
  horse.y = worldH.value - dims.GROUND_HEIGHT - dims.HORSE_H
  horse.w = dims.HORSE_W
  horse.h = dims.HORSE_H
  horse.vy = 0
  frame = 0
  obstacles.value = []
  score.value = 0
  gameOver.value = false
  running.value = true
  cancelAnimationFrame(rafId)
  rafId = requestAnimationFrame(loop)
  speed = baseSpeed()
}

function jump() {
  if (!running.value) {
    if (gameOver.value) return
    resetGame()
    return
  }
  const groundY = worldH.value - dims.GROUND_HEIGHT - horse.h
  if (Math.abs(horse.y - groundY) < 2) { horse.vy = -10.5; vibrate(10) }
}

function onKey(e) {
  if (e.code === 'Space' || e.code === 'ArrowUp') { e.preventDefault(); jump() }
  if (e.code === 'Enter' && gameOver.value) resetGame()
}

function onVis(){
  if (document.hidden && running.value) {
    cancelAnimationFrame(rafId)
  } else if (!document.hidden && running.value) {
    rafId = requestAnimationFrame(loop)
  }
}

function clamp(n, min, max){ return Math.max(min, Math.min(max, n)) }
function landingGapPx(){ return Math.max(56, Math.round(horse.w * 1.4)) } // espaço de pouso
function afterWaterGapPx(){ return 140 + Math.round(speed * 6) }         // respiro após água

// largura da água limitada e proporcional à tela
function waterWidthPx(){
  const maxFrac = 0.22;                       // máx 22% da largura da pista
  const maxByScreen = Math.round(worldW.value * maxFrac);
  const base = 90 + rand(0, 40);              // 90–130 px
  return clamp(base, 80, Math.min(150, maxByScreen));
}


/* ========= Estilos ========= */
const fontScale = 32

const styles = reactive({
  container: { width: '100%', minHeight: '300px', display: 'flex', justifyContent: 'center', padding: '12px' },
  world: {
    position: 'relative',
    borderRadius: '16px',
    boxShadow: '0 10px 20px rgba(0,0,0,.12)',
    overflow: 'hidden',
    background: 'linear-gradient(180deg, #bae6fd 0%, #93c5fd 100%)',
    userSelect: 'none',
    touchAction: 'manipulation'
  },
  ground: {
    position: 'absolute', left: '0px', right: '0px', bottom: '0px',
    height: '40px',
    background: '#059669',
    boxShadow: '0 -2px 6px rgba(0,0,0,.08) inset',
    zIndex: 2
  },
  groundPattern: {
    position: 'absolute', top: 0, right: 0, bottom: 0, left: 0,
    opacity: .30,
    backgroundImage: 'repeating-linear-gradient(45deg, rgba(0,0,0,0) 0 10px, rgba(0,0,0,.18) 10px 20px)'
  },
  hud: { position: 'absolute', top: '8px', left: '8px', right: '8px', display: 'flex', justifyContent: 'space-between', color: '#0f172a', fontFamily: 'system-ui, sans-serif', fontSize: '14px', zIndex: 5 },
  hudChip: { background: 'rgba(255,255,255,.75)', padding: '4px 8px', borderRadius: '12px', backdropFilter: 'blur(2px)', fontWeight: 600 },
  startOverlay: { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', textAlign: 'center', color: '#1f2937', fontFamily: 'system-ui, sans-serif', padding: '12px' },
  startBtn: { padding: '8px 14px', borderRadius: '16px', background: '#059669', color: '#fff', border: 'none', boxShadow: '0 6px 14px rgba(0,0,0,.15)', cursor: 'pointer', touchAction: 'manipulation' },
  over: { position: 'absolute', inset: 0, background: 'rgba(0,0,0,.4)', color: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', textAlign: 'center', fontFamily: 'system-ui, sans-serif', padding: '12px' },
  overBtn: { padding: '10px 18px', borderRadius: '16px', background: '#fff', color: '#111827', border: 'none', cursor: 'pointer' },
  obstacle: { position: 'absolute', background: '#9a3412', border: '1px solid rgba(124,45,18,.5)', borderTopLeftRadius: '6px', borderTopRightRadius: '6px', boxShadow: 'inset 0 2px 0 rgba(0,0,0,.25)', zIndex: 3 },
  jumpBtn: { position: 'absolute', right: '10px', bottom: '10px', width: '56px', height: '56px', borderRadius: '999px', background: '#10b981', color: '#fff', border: 'none', fontSize: '24px', boxShadow: '0 6px 14px rgba(0,0,0,.18)', zIndex: 6, touchAction: 'manipulation' },
})

const worldStyle = computed(() => ({
  ...styles.world,
  width: worldW.value + 'px',
  height: worldH.value + 'px',
}))

const skyStyle = { position: 'absolute', inset: 0, opacity: .2, pointerEvents: 'none', zIndex: 1 }
const horseStyle = computed(() => ({
  position: 'absolute',
  zIndex: 4,
  filter: 'drop-shadow(0 2px 2px rgba(0,0,0,.3))',
  transform: `translate(${horse.x}px, ${horse.y}px)`
}))
const obstacleTopStyle = { position: 'absolute', left: 0, right: 0, height: '4px', top: '-4px', background: 'rgba(254,240,138,.9)', borderTopLeftRadius: '6px', borderTopRightRadius: '6px' }

/* estilos calculados para cada obstáculo */
function barStyle(o){
  return {
    ...styles.obstacle,
    transform: `translate(${o.x}px, ${o.y}px)`,
    width: o.w + 'px',
    height: o.h + 'px',
  }
}
function waterStyle(o){
  return {
    position: 'absolute',
    transform: `translate(${o.x}px, ${o.y}px)`,
    width: o.w + 'px',
    height: o.h + 'px',
    background: 'linear-gradient(180deg, rgba(59,130,246,.9), rgba(37,99,235,.9))',
    border: '1px solid rgba(30,64,175,.6)',
    boxShadow: 'inset 0 2px 6px rgba(255,255,255,.35)',
    borderRadius: '6px',
    zIndex: 3,
    backgroundImage: 'repeating-linear-gradient(90deg, rgba(255,255,255,.15) 0 6px, rgba(255,255,255,0) 6px 12px)',
    backgroundBlendMode: 'overlay'
  }
}
</script>

<style scoped>
:host, * { -webkit-tap-highlight-color: transparent; }
</style>
